import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { groupPlayAPI } from '../../services/api';
import { GroupPlay, GroupPlayInstance, GroupPlayBooking, GroupPlayBookingStatus } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface GroupPlayBookingsDialogProps {
  open: boolean;
  onClose: () => void;
  groupPlay: GroupPlay;
  instance: GroupPlayInstance | null;
  onRefresh: () => void;
}

const GroupPlayBookingsDialog: React.FC<GroupPlayBookingsDialogProps> = ({
  open,
  onClose,
  groupPlay,
  instance,
  onRefresh,
}) => {
  const [bookings, setBookings] = useState<GroupPlayBooking[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [instances, setInstances] = useState<GroupPlayInstance[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (instance) {
        // View bookings for a specific instance
        setSelectedInstanceId(instance.id);
        fetchBookings(instance.id);
      } else if (groupPlay.recurringPattern) {
        // Fetch all instances for recurring group play
        fetchInstances();
      } else {
        // Fetch instances for one-time group play
        fetchInstances();
      }
    }
  }, [open, groupPlay.id, instance]);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await groupPlayAPI.getGroupPlayInstances(groupPlay.id);
      const instancesList = response.data;
      setInstances(instancesList);
      
      // Select the first upcoming instance by default
      if (instancesList.length > 0) {
        const upcomingInstance = instancesList.find((inst: GroupPlayInstance) => 
          inst.status === 'SCHEDULED'
        ) || instancesList[0];
        setSelectedInstanceId(upcomingInstance.id);
        fetchBookings(upcomingInstance.id);
      }
    } catch (error: any) {
      console.error('Error fetching instances:', error);
      setError('Failed to load instances');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async (instanceId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await groupPlayAPI.getGroupPlayInstanceBookings(groupPlay.id, instanceId);
      setBookings(response.data);
    } catch (error: any) {
      console.error('Error fetching bookings:', error);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (booking: GroupPlayBooking) => {
    // Calculate if within cancellation window
    const selectedInstance = instances.find(inst => inst.id === booking.groupPlayInstanceId);
    let withinWindow = false;
    
    if (selectedInstance) {
      try {
        const [year, month, day] = selectedInstance.sessionDate.split('-').map(Number);
        const [hours, minutes] = selectedInstance.startTime.split(':').map(Number);
        const sessionDate = new Date(year, month - 1, day, hours, minutes, 0);
        const now = new Date();
        const hoursUntil = (sessionDate.getTime() - now.getTime()) / (1000 * 60 * 60);
        withinWindow = hoursUntil < 24; // Default 24 hour window
      } catch (error) {
        console.error('Error calculating cancellation window:', error);
      }
    }

    const confirmMessage = withinWindow
      ? `WARNING: This cancellation is within 24 hours of the session. The transaction will NOT be cancelled and fees will apply.\n\nAre you sure you want to cancel the booking for ${booking.guestName || booking.memberName}?`
      : `Are you sure you want to cancel the booking for ${booking.guestName || booking.memberName}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError(null);
      await groupPlayAPI.cancelGroupPlayInstanceBooking(
        groupPlay.id,
        booking.groupPlayInstanceId,
        booking.id
      );
      setSuccessMessage('Booking cancelled successfully');
      // Refresh bookings
      if (selectedInstanceId) {
        fetchBookings(selectedInstanceId);
      }
      onRefresh();
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      setError(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handleInstanceChange = (instanceId: string) => {
    setSelectedInstanceId(instanceId);
    fetchBookings(instanceId);
  };

  const getStatusColor = (status: GroupPlayBookingStatus) => {
    switch (status) {
      case GroupPlayBookingStatus.CONFIRMED:
        return 'success';
      case GroupPlayBookingStatus.WAITLIST:
        return 'warning';
      case GroupPlayBookingStatus.CANCELLED:
        return 'error';
      case GroupPlayBookingStatus.ATTENDED:
        return 'info';
      case GroupPlayBookingStatus.NO_SHOW:
        return 'default';
      default:
        return 'default';
    }
  };

  const confirmedBookings = bookings.filter(b => b.status === GroupPlayBookingStatus.CONFIRMED);
  const waitlistBookings = bookings.filter(b => b.status === GroupPlayBookingStatus.WAITLIST);
  const cancelledBookings = bookings.filter(b => b.status === GroupPlayBookingStatus.CANCELLED);

  const selectedInstance = instances.find(inst => inst.id === selectedInstanceId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Bookings for: {groupPlay.name}
        {instance && (
          <Typography variant="body2" color="text.secondary">
            Session: {formatDate(instance.sessionDate)} at {formatTimeString(instance.startTime)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Instance selector for recurring group plays */}
        {!instance && instances.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Session:
            </Typography>
            <Tabs
              value={selectedInstanceId}
              onChange={(e, value) => handleInstanceChange(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {instances.map((inst) => (
                <Tab
                  key={inst.id}
                  value={inst.id}
                  label={`${formatDate(inst.sessionDate)} - ${formatTimeString(inst.startTime)}`}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary */}
            {selectedInstance && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Capacity: {selectedInstance.confirmedCount || 0} / {selectedInstance.capacity} confirmed
                  {waitlistBookings.length > 0 && ` | ${waitlistBookings.length} on waitlist`}
                </Typography>
              </Box>
            )}

            {/* Tabs for booking status */}
            <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 2 }}>
              <Tab label={`Confirmed (${confirmedBookings.length})`} />
              <Tab label={`Waitlist (${waitlistBookings.length})`} />
              <Tab label={`Cancelled (${cancelledBookings.length})`} />
            </Tabs>

            {/* Bookings Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    {selectedTab === 1 && <TableCell>Position</TableCell>}
                    <TableCell>Booked At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedTab === 0 ? confirmedBookings :
                    selectedTab === 1 ? waitlistBookings :
                    cancelledBookings).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={selectedTab === 1 ? 7 : 6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No bookings found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (selectedTab === 0 ? confirmedBookings :
                      selectedTab === 1 ? waitlistBookings :
                      cancelledBookings).map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>
                          {booking.guestName || booking.memberName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {booking.guestEmail || booking.memberEmail || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.guestName ? 'Guest' : 'Member'}
                            size="small"
                            color={booking.guestName ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColor(booking.status)}
                          />
                        </TableCell>
                        {selectedTab === 1 && (
                          <TableCell>{booking.waitlistPosition || '-'}</TableCell>
                        )}
                        <TableCell>
                          {new Date(booking.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          {booking.status === GroupPlayBookingStatus.CONFIRMED && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelBooking(booking)}
                              title="Cancel Booking"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupPlayBookingsDialog;
